/**
 * Review Step
 *
 * Final step of the intake wizard - shows summary of all collected information
 * and allows editing before submission.
 */

import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Divider,
  GlassChip,
  GlassIconButton,
  GlassTooltip,
  Grid,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import type { IntakeFormData } from '../IntakeWizard';

interface ReviewStepProps {
  formData: IntakeFormData;
  onEdit: (step: number) => void;
}

function formatCurrency(value: string, notSpecified: string): string {
  if (!value) return notSpecified;
  const num = parseFloat(value);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function formatLabel(value: string, notSpecified: string): string {
  if (!value) return notSpecified;
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

interface SectionProps {
  title: string;
  editTooltip: string;
  step: number;
  onEdit: (step: number) => void;
  children: React.ReactNode;
}

function Section({ title, editTooltip, step, onEdit, children }: SectionProps) {
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <GlassTooltip title={editTooltip}>
          <GlassIconButton size="small" onClick={() => onEdit(step)}>
            <EditIcon fontSize="small" />
          </GlassIconButton>
        </GlassTooltip>
      </Box>
      {children}
      <Divider sx={{ mt: 2 }} />
    </Box>
  );
}

interface InfoRowProps {
  label: string;
  value: string | React.ReactNode;
}

function InfoRow({ label, value }: InfoRowProps) {
  return (
    <Grid container sx={{ py: 0.5 }}>
      <Grid size={6}>
        <Typography variant="body2" color="textSecondary">
          {label}
        </Typography>
      </Grid>
      <Grid size={6}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {value || '-'}
        </Typography>
      </Grid>
    </Grid>
  );
}

export default function ReviewStep({ formData, onEdit }: ReviewStepProps) {
  const { t } = useI18n();
  const notSpecified = t('common.notSpecified');
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <CheckIcon color="success" />
        <Typography variant="h6">{t('deal.reviewYourInformation')}</Typography>
      </Box>
      <Typography color="textSecondary" sx={{ mb: 3 }}>
        {t('consumer.onboarding.reviewDescription')}
      </Typography>

      {/* Business Type Section */}
      <Section
        title={t('consumer.onboarding.businessInformation')}
        editTooltip={t('consumer.onboarding.editSection')}
        step={0}
        onEdit={onEdit}
      >
        <InfoRow
          label={t('consumer.onboarding.businessType')}
          value={formatLabel(formData.businessType, notSpecified)}
        />
        {formData.businessType !== 'individual' && formData.businessType && (
          <>
            <InfoRow label={t('consumer.onboarding.businessName')} value={formData.businessName} />
            <InfoRow label={t('consumer.onboarding.stateOfFormation')} value={formData.stateOfFormation} />
            <InfoRow
              label={t('consumer.onboarding.yearsInBusiness')}
              value={
                formData.yearsInBusiness ? t('consumer.onboarding.yearsLabel', { years: formData.yearsInBusiness }) : ''
              }
            />
          </>
        )}
      </Section>

      {/* Eligibility Section */}
      <Section
        title={t('consumer.onboarding.loanAndPropertyDetails')}
        editTooltip={t('consumer.onboarding.editSection')}
        step={1}
        onEdit={onEdit}
      >
        <InfoRow label={t('consumer.onboarding.loanPurpose')} value={formatLabel(formData.loanPurpose, notSpecified)} />
        <InfoRow label={t('loan.propertyType')} value={formatLabel(formData.propertyType, notSpecified)} />
        <InfoRow
          label={t('consumer.onboarding.estimatedLoanAmount')}
          value={formatCurrency(formData.estimatedLoanAmount, notSpecified)}
        />
        <InfoRow
          label={t('consumer.onboarding.creditScoreRange')}
          value={
            formData.creditScoreRange && (
              <GlassChip
                size="small"
                label={formatLabel(formData.creditScoreRange, notSpecified)}
                color={
                  formData.creditScoreRange === 'excellent'
                    ? 'success'
                    : formData.creditScoreRange === 'good'
                      ? 'info'
                      : formData.creditScoreRange === 'fair'
                        ? 'warning'
                        : 'error'
                }
              />
            )
          }
        />
        <InfoRow
          label={t('consumer.onboarding.investmentExperience')}
          value={
            formData.hasExperience
              ? t('consumer.onboarding.propertiesExperience', { count: formData.numberOfProperties || '0' })
              : t('consumer.onboarding.noPriorExperience')
          }
        />
      </Section>

      {/* Preferences Section */}
      <Section
        title={t('consumer.onboarding.communicationPreferences')}
        editTooltip={t('consumer.onboarding.editSection')}
        step={2}
        onEdit={onEdit}
      >
        <InfoRow
          label={t('consumer.onboarding.preferredContactMethod')}
          value={formatLabel(formData.preferredContact, notSpecified)}
        />
        <InfoRow
          label={t('consumer.onboarding.timezone')}
          value={formData.timezone?.replace('America/', '').replace('_', ' ')}
        />
        <InfoRow
          label={t('consumer.onboarding.bestTimeToContact')}
          value={formatLabel(formData.bestTimeToContact, notSpecified)}
        />
        <InfoRow
          label={t('consumer.onboarding.marketingUpdates')}
          value={
            <GlassChip
              size="small"
              label={formData.marketingOptIn ? t('consumer.onboarding.optedIn') : t('consumer.onboarding.optedOut')}
              color={formData.marketingOptIn ? 'success' : 'default'}
            />
          }
        />
      </Section>

      <Box
        sx={{
          p: 2,
          bgcolor: 'success.main',
          color: 'success.contrastText',
          borderRadius: 1,
          textAlign: 'center',
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {t('consumer.onboarding.allSet')}
        </Typography>
      </Box>
    </Box>
  );
}
